import React, { memo, useCallback, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Button } from "@mui/material";
import InlineMessage from "../../../../common/InlineMessage/InlineMessage";
import WebLinkDialog from "../WebLinkDialog/WebLinkDialog";
import SelectContentDialog from "../SelectContentDialog/SelectContentDialog";
import "./ContentRedirectOption.css";

/* Module-level helper kept outside the component so its identity is stable
 * across renders and React.memo'd children can rely on referential equality. */
const stopPropagation = (e) => e.stopPropagation();

/* ─── Content redirect options + enum maps ───────────────────────────── */
export const CONTENT_REDIRECT_OPTIONS = [
  {
    value: "show_all",
    label: "Show All Content",
    description:
      "Banner click shows all available content for this distributor.",
  },
  {
    value: "specific_content",
    label: "Link Specific Content / Catalog / Bundle",
    description: "Select up to 10 items.",
    actionLabel: "Select Content",
    required: true,
  },
  {
    value: "external_web",
    label: "External Web Content",
    description: "Redirect subscriber to an external URL.",
    actionLabel: "Add Link",
    required: true,
  },
];

/* ContentRedirectType enum — string key → API integer.
 * Radio order maps 1:1 to the API contract:
 *   1 = Show All Content
 *   2 = Link Specific Content / Catalog / Bundle  (sends LinkedContentIds[])
 *   3 = External Web Content                       (sends ExternalUrl) */
export const CONTENT_REDIRECT_TO_INT = {
  show_all: 1,
  specific_content: 2,
  external_web: 3,
};

/* Reverse map: API integer → form string key */
export const INT_TO_CONTENT_REDIRECT = {
  1: "show_all",
  2: "specific_content",
  3: "external_web",
};

/**
 * ContentRedirectOption
 * Bordered card containing a radio group for selecting the content-redirect
 * strategy. Each option shows a label + description; selecting "Link Specific
 * Content" reveals a "Select Content" button and selecting "External Web
 * Content" reveals an "Add Link" button plus the External URL input.
 *
 * Render perf: each row is rendered through `RadioOption` (memoized) and all
 * handlers passed down are wrapped in useCallback so an unrelated parent
 * re-render — or a state change that only affects one row — doesn't repaint
 * the other rows or the dialogs.
 */

/* ── Memoized row component ──────────────────────────────────────────── */
const RadioOption = memo(function RadioOption({
  opt,
  isSelected,
  isViewMode,
  externalUrl,
  linkedCount,
  onSelect,
  onAction,
}) {
  const classes = [
    "radio-opt",
    isSelected ? "selected" : "",
    isViewMode ? "is-disabled" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const radioId = `cr_${opt.value}`;

  const handleRadioChange = useCallback(() => {
    onSelect(opt.value);
  }, [onSelect, opt.value]);

  const handleActionClick = useCallback(
    (e) => {
      e.stopPropagation();
      onAction(opt.value);
    },
    [onAction, opt.value],
  );

  const actionLabel = useMemo(() => {
    if (opt.value === "external_web" && externalUrl) return "Update Link";
    if (opt.value === "specific_content" && linkedCount > 0)
      return "Update Content";
    return opt.actionLabel;
  }, [opt.value, opt.actionLabel, externalUrl, linkedCount]);

  return (
    <label htmlFor={radioId} className={classes}>
      <input
        type="radio"
        id={radioId}
        name="contentRedirect"
        value={opt.value}
        checked={isSelected}
        onChange={handleRadioChange}
        disabled={isViewMode}
        onClick={stopPropagation}
        className="radio-opt__input"
      />
      <div className="radio-opt__body">
        <span className="radio-opt__label">{opt.label}</span>
        {opt.description && (
          <span className="radio-opt__description">
            {opt.description}
            {opt.required && (
              <span className="content-redirect-required"> *</span>
            )}
          </span>
        )}
        {opt.actionLabel && isSelected && !isViewMode && (
          <div className="radio-opt__action-row">
            {opt.value === "specific_content" && linkedCount > 0 && (
              <span className="radio-opt__linked-count">
                {linkedCount} item{linkedCount === 1 ? "" : "s"} linked
              </span>
            )}
            <Button
              size="small"
              variant="outlined"
              className="radio-opt__action"
              onClick={handleActionClick}
            >
              {actionLabel}
            </Button>
          </div>
        )}
        {opt.value === "external_web" && isSelected && externalUrl && (
          <div className="radio-opt__url-wrap">
            <label htmlFor={`${radioId}_url`} className="radio-opt__url-label">
              External URL <span className="content-redirect-required">*</span>
            </label>
            <input
              id={`${radioId}_url`}
              className="form-label form-control"
              type="url"
              value={externalUrl}
              readOnly
              disabled
            />
          </div>
        )}
      </div>
    </label>
  );
});

RadioOption.propTypes = {
  opt: PropTypes.shape({
    value: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    description: PropTypes.string,
    actionLabel: PropTypes.string,
    required: PropTypes.bool,
  }).isRequired,
  isSelected: PropTypes.bool.isRequired,
  isViewMode: PropTypes.bool.isRequired,
  externalUrl: PropTypes.string,
  linkedCount: PropTypes.number,
  onSelect: PropTypes.func.isRequired,
  onAction: PropTypes.func.isRequired,
};

RadioOption.defaultProps = {
  externalUrl: "",
  linkedCount: 0,
};

const ContentRedirectOption = ({
  value,
  onChange,
  externalUrl,
  onExternalUrlChange,
  selectedContent,
  onSelectedContentChange,
  isViewMode,
  error,
  distributorId,
  onSelectContent,
  onAddLink,
}) => {
  const [isWebLinkOpen, setIsWebLinkOpen] = useState(false);
  const [isSelectContentOpen, setIsSelectContentOpen] = useState(false);

  const linkedCount = selectedContent ? selectedContent.length : 0;

  const handleRadioChange = useCallback(
    (nextValue) => {
      if (
        value === "external_web" &&
        nextValue !== "external_web" &&
        externalUrl
      ) {
        onExternalUrlChange("");
      }
      if (
        value === "specific_content" &&
        nextValue !== "specific_content" &&
        linkedCount > 0
      ) {
        onSelectedContentChange([]);
      }
      onChange(nextValue);
    },
    [
      value,
      externalUrl,
      linkedCount,
      onExternalUrlChange,
      onSelectedContentChange,
      onChange,
    ],
  );

  const handleAction = useCallback(
    (optionValue) => {
      if (optionValue === "specific_content") {
        setIsSelectContentOpen(true);
        if (typeof onSelectContent === "function") onSelectContent();
      }
      if (optionValue === "external_web") {
        setIsWebLinkOpen(true);
        if (typeof onAddLink === "function") onAddLink();
      }
    },
    [onSelectContent, onAddLink],
  );

  const handleWebLinkSubmit = useCallback(
    (nextUrl) => {
      onExternalUrlChange(nextUrl);
    },
    [onExternalUrlChange],
  );

  const handleWebLinkClose = useCallback(() => {
    setIsWebLinkOpen(false);
  }, []);

  const handleContentSubmit = useCallback(
    (items) => {
      onSelectedContentChange(items);
    },
    [onSelectedContentChange],
  );

  const handleContentDialogClose = useCallback(() => {
    setIsSelectContentOpen(false);
  }, []);

  return (
    <div className="content-redirect-root">
      <div className="content-redirect-header">
        Content Redirect <span className="content-redirect-required">*</span>
      </div>

      {CONTENT_REDIRECT_OPTIONS.map((opt) => (
        <RadioOption
          key={opt.value}
          opt={opt}
          isSelected={value === opt.value}
          isViewMode={isViewMode}
          externalUrl={opt.value === "external_web" ? externalUrl : ""}
          linkedCount={opt.value === "specific_content" ? linkedCount : 0}
          onSelect={handleRadioChange}
          onAction={handleAction}
        />
      ))}

      <InlineMessage error={error || ""} />

      <WebLinkDialog
        open={isWebLinkOpen}
        initialUrl={externalUrl}
        onClose={handleWebLinkClose}
        onSubmit={handleWebLinkSubmit}
      />

      <SelectContentDialog
        open={isSelectContentOpen}
        initialSelection={selectedContent}
        onClose={handleContentDialogClose}
        onSubmit={handleContentSubmit}
        distributorId={distributorId}
      />
    </div>
  );
};

ContentRedirectOption.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  externalUrl: PropTypes.string,
  onExternalUrlChange: PropTypes.func.isRequired,
  selectedContent: PropTypes.array,
  onSelectedContentChange: PropTypes.func,
  isViewMode: PropTypes.bool,
  error: PropTypes.string,
  distributorId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onSelectContent: PropTypes.func,
  onAddLink: PropTypes.func,
};

ContentRedirectOption.defaultProps = {
  value: "",
  externalUrl: "",
  selectedContent: [],
  onSelectedContentChange: () => {},
  isViewMode: false,
  error: "",
  distributorId: null,
  onSelectContent: undefined,
  onAddLink: undefined,
};

export default memo(ContentRedirectOption);
